import { TrashIcon } from '@src/components/icons/TrashIcon';
import { DownloadIcon } from '@src/components/icons/DownloadIcon';
import { ArchiveIcon } from '@src/components/icons/ArchiveIcon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AdminGoodsInput,
  goodsItemLink,
  type AdminAdvertiser,
  type AdminGoodsItem,
} from '@lub0v/santa-utils';
import { MarketExtensionGuide } from './MarketExtensionGuide';
import { Button } from '@src/components/Button/Button';
import { santaClient } from '@src/state/client';
import './AdminGoods.css';
import { showDialog, closeDialog } from '@src/components/Dialog/Dialog';
import { editAdvertiser } from './AdvertiserEditor';
import { parseGoodsClipboard } from './goodsClipboard';
import { GoodsImageEditor, goodsImageStyle } from './GoodsImageEditor';

export function AdminGoods({
  onDenied,
  isSuperAdmin,
  onPendingCount,
}: {
  onDenied: () => void;
  isSuperAdmin: boolean;
  onPendingCount: (count: number) => void;
}) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const words = (en: string, russian: string) => (ru ? russian : en);
  const [rublesOnly, setRublesOnly] = useState(true);
  const [advertisers, setAdvertisers] = useState<AdminAdvertiser[]>([]);
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState<number | null>(null);
  const [newAdvertiser, setNewAdvertiser] = useState(false);
  const [items, setItems] = useState<AdminGoodsItem[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (isSuperAdmin && !loading)
      onPendingCount(items.filter((item) => item.active && item.status === 'pending').length);
  }, [items, loading, isSuperAdmin, onPendingCount]);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState<AdminGoodsInput>(() => new AdminGoodsInput());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [importNote, setImportNote] = useState('');
  const form = useRef<HTMLFormElement>(null);
  const denied = useRef(onDenied);
  useEffect(() => {
    denied.current = onDenied;
  }, [onDenied]);
  useEffect(() => {
    let cancelled = false;
    async function loadAllGoods() {
      const allItems = new Map<number, AdminGoodsItem>();
      try {
        const advertiserResult = await santaClient.getAdminAdvertisers({ page: 1 });
        if (cancelled) return;
        if (!advertiserResult.ok || !advertiserResult.data) {
          if ([401, 403].includes(advertiserResult.error?.status || 0)) denied.current();
          throw new Error();
        }
        setAdvertisers(advertiserResult.data);
        setSelectedAdvertiserId((old) => old ?? advertiserResult.data![0]?.id ?? null);
        // Keep API requests bounded while showing the entire catalog in one view.
        for (let page = 1; !cancelled; page += 1) {
          const result = await santaClient.getAdminGoods({ page });
          if (cancelled) return;
          if (!result.ok || !result.data) {
            if ([401, 403].includes(result.error?.status || 0)) denied.current();
            setError(ru ? 'Не удалось загрузить товары.' : 'Could not load goods.');
            return;
          }
          for (const item of result.data.items) allItems.set(item.id, item);
          if (!result.data.hasMore) {
            setItems([...allItems.values()]);
            setRublesOnly(result.data.rublesOnly);
            return;
          }
        }
      } catch {
        if (!cancelled) setError(ru ? 'Ошибка соединения.' : 'Connection failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadAllGoods();
    return () => {
      cancelled = true;
    };
  }, [revision, ru]);
  const fileInput = useRef<HTMLInputElement>(null);
  const dirty = Boolean(
    draft.erid ||
    draft.name ||
    draft.description ||
    draft.price ||
    draft.image ||
    draft.link ||
    draft.currency !== 'RUB' ||
    draft.imageConfig.x !== 50 ||
    draft.imageConfig.y !== 50 ||
    draft.imageConfig.scale !== 1,
  );
  const selectedAdvertiser = advertisers.find((item) => item.id === selectedAdvertiserId);
  const visibleItems = newAdvertiser
    ? []
    : items.filter((item) => (item.goodsAdvertiserId ?? null) === selectedAdvertiserId);
  function advertiserSaved(item: AdminAdvertiser) {
    setAdvertisers((old) =>
      old.some((row) => row.id === item.id)
        ? old.map((row) => (row.id === item.id ? item : row))
        : [...old, item],
    );
    setSelectedAdvertiserId(item.id);
    setNewAdvertiser(false);
  }
  function pendingBadge(rows: AdminGoodsItem[]) {
    const count = rows.filter((item) => item.active && item.status === 'pending').length;
    return isSuperAdmin && count > 0 ? (
      <span
        className="admin-goods__pending-count"
        title={words('Pending approval', 'Ожидают одобрения')}
        aria-label={`${words('Pending approval', 'Ожидают одобрения')}: ${count}`}
      >
        {count}
      </span>
    ) : null;
  }
  function resetForm() {
    setImportNote('');
    setPasteOpen(false);
    setPasteText('');
    setDraft(new AdminGoodsInput());
    setEditingId(null);
    setError('');
    if (fileInput.current) fileInput.current.value = '';
    form.current?.querySelector<HTMLInputElement>('input:not([type=file])')?.focus();
  }
  function open(item?: AdminGoodsItem) {
    setImportNote('');
    setEditingId(item?.id ?? null);
    setDraft(
      item
        ? {
            ...new AdminGoodsInput(),
            ...item,
            description: item.description || '',
            erid: item.erid || '',
            image: item.image || '',
            link: item.link || '',
            price: item.price ?? '',
            currency: rublesOnly ? 'RUB' : item.currency || 'RUB',
            imageConfig: {
              x: item.imageConfig?.x ?? 50,
              y: item.imageConfig?.y ?? 50,
              scale: item.imageConfig?.scale ?? 1,
            },
          }
        : new AdminGoodsInput(),
    );
    setError('');
    if (fileInput.current) fileInput.current.value = '';
    form.current?.querySelector<HTMLInputElement>('input:not([type=file])')?.focus();
  }
  function change(key: keyof AdminGoodsInput, value: string) {
    setDraft((old) => ({ ...old, [key]: value }));
  }
  async function upload(file?: File) {
    if (!file || busy || uploading || newAdvertiser || !selectedAdvertiser) return;
    if (
      !file.size ||
      file.size > 2 * 1024 * 1024 ||
      !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)
    ) {
      setError(
        words(
          'Choose a JPG, PNG, WebP or GIF up to 2 MB.',
          'Выберите JPG, PNG, WebP или GIF до 2 МБ.',
        ),
      );
      return;
    }
    setUploading(true);
    setError('');
    try {
      const result = await santaClient.uploadAdminGoods(file, file.name, file.type);
      if ([401, 403].includes(result.error?.status || 0)) denied.current();
      if (!result.ok || !result.data) throw new Error();
      change('image', result.data.publicUrl);
    } catch {
      setError(
        words(
          'Upload failed. Please try again.',
          'Не удалось загрузить изображение. Попробуйте ещё раз.',
        ),
      );
    } finally {
      setUploading(false);
    }
  }
  function applyClipboard(text: string) {
    try {
      const item = parseGoodsClipboard(text);
      setDraft((old) => ({ ...old, ...item }));
      setError('');
      setPasteOpen(false);
      setPasteText('');
      setImportNote(
        words(
          'Product pasted. Check the image, selected variant and price; it may require Yandex Pay. Missing fields were cleared for review.',
          'Товар вставлен. Проверьте фото, вариант и цену: она может требовать оплату через Яндекс Пэй. Ненайденные поля очищены для проверки.',
        ),
      );
    } catch {
      setPasteOpen(true);
      setError(
        words(
          'Paste product data copied by the Santa extension.',
          'Вставьте данные товара, скопированные расширением Santa.',
        ),
      );
    }
  }
  async function pasteProduct() {
    try {
      applyClipboard(await navigator.clipboard.readText());
    } catch {
      setPasteOpen(true);
    }
  }
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (busy || uploading || newAdvertiser || !selectedAdvertiser) return;
    setBusy(true);
    setError('');
    try {
      const result =
        editingId === null
          ? await santaClient.createAdminGoods({
              ...draft,
              goodsAdvertiserId: selectedAdvertiserId,
            })
          : await santaClient.updateAdminGoods({ ...draft, id: String(editingId) });
      if (!result.ok) {
        if ([401, 403].includes(result.error?.status || 0)) denied.current();
        throw new Error();
      }
      if (result.data) {
        const item = result.data;
        setItems((old) =>
          editingId === null ? [item, ...old] : old.map((row) => (row.id === item.id ? item : row)),
        );
      }
      resetForm();
      setLoading(true);
      setRevision((n) => n + 1);
    } catch {
      setError(
        words(
          'Could not save. Check the fields and try again.',
          'Не удалось сохранить. Проверьте поля и попробуйте ещё раз.',
        ),
      );
    } finally {
      setBusy(false);
    }
  }
  function confirmArchive() {
    const item = items.find((row) => row.id === editingId);
    if (!item || busy || uploading) return;
    const restore = !item.active;
    showDialog({
      title: restore
        ? words('Restore item?', 'Восстановить товар?')
        : words('Archive item?', 'Архивировать товар?'),
      children: (
        <>
          <p>
            <strong>{item.name}</strong>
          </p>
          <p>
            {restore
              ? words(
                  'The item will return to the catalog and require approval again.',
                  'Товар вернётся в каталог и снова потребует одобрения.',
                )
              : words(
                  'The item will be removed from public display. Clicks, statistics and all other item data will stay intact. You can restore it from the archive later.',
                  'Товар будет скрыт из публичного каталога. Клики, статистика и все остальные данные сохранятся. Позже товар можно будет восстановить из архива.',
                )}
          </p>
          {dirty && (
            <p>
              {words(
                'Unsaved form changes will be discarded.',
                'Несохранённые изменения формы будут отменены.',
              )}
            </p>
          )}
        </>
      ),
      cancel: words('Cancel', 'Отмена'),
      submit: restore ? words('Restore', 'Восстановить') : words('Archive', 'Архивировать'),
      submitVariant: restore ? 'primary' : 'danger',
      onSubmit: async () => {
        setBusy(true);
        try {
          const result = restore
            ? await santaClient.restoreAdminGoods({ id: String(item.id) })
            : await santaClient.archiveAdminGoods({ id: String(item.id) });
          if (!result.ok || !result.data) {
            if ([401, 403].includes(result.error?.status || 0)) denied.current();
            throw new Error();
          }
          setItems((old) => old.map((row) => (row.id === item.id ? result.data! : row)));
          resetForm();
        } catch {
          setError(
            words(
              'Could not update the archive. Please try again.',
              'Не удалось изменить архив. Попробуйте ещё раз.',
            ),
          );
          closeDialog();
          throw new Error('Archive update failed');
        } finally {
          setBusy(false);
        }
      },
    });
  }
  async function approve(item: AdminGoodsItem) {
    if (busy || uploading) return;
    setBusy(true);
    setError('');
    try {
      const result = await santaClient.approveAdminGoods({ id: String(item.id) });
      if (!result.ok || !result.data) {
        if ([401, 403].includes(result.error?.status || 0)) denied.current();
        throw new Error();
      }
      const approved = result.data;
      setItems((old) => old.map((row) => (row.id === approved.id ? approved : row)));
    } catch {
      setError(
        words(
          'Could not approve this item. Please try again.',
          'Не удалось одобрить товар. Попробуйте ещё раз.',
        ),
      );
    } finally {
      setBusy(false);
    }
  }
  function managedCard(item: AdminGoodsItem) {
    return (
      <div key={item.id} className={editingId === item.id ? 'admin-goods__draft' : ''}>
        {editingId === item.id && (
          <span className="admin-goods__draft-label">
            {words('Editing · Not saved', 'Редактирование · Не сохранено')}
          </span>
        )}
        {card(editingId === item.id ? draft : item, editingId === item.id)}
        <div className="admin-goods__moderation">
          <span className={`admin-goods__status admin-goods__status--${item.status}`}>
            {item.status === 'approved'
              ? words('Approved', 'Одобрен')
              : words('Pending approval', 'На рассмотрении')}
          </span>
          <button
            type="button"
            className="admin-goods__edit"
            disabled={busy || uploading || dirty}
            onClick={() => open(item)}
          >
            {words('Edit', 'Редактировать')}
          </button>
        </div>
        {!item.active && <span>{words('Archived', 'В архиве')}</span>}
        {isSuperAdmin && item.active && item.status !== 'approved' && (
          <Button
            type="button"
            variant="primary"
            size="small"
            fullWidth
            disabled={busy || uploading || editingId === item.id}
            onClick={() => void approve(item)}
          >
            {words('Approve', 'Одобрить')}
          </Button>
        )}
      </div>
    );
  }
  function card(item: AdminGoodsInput, preview = false) {
    const c = {
      x: item.imageConfig?.x ?? 50,
      y: item.imageConfig?.y ?? 50,
      scale: item.imageConfig?.scale ?? 1,
    };
    return (
      <article className="admin-goods__card">
        <div className="admin-goods__image">
          {item.image ? (
            <img
              src={item.image}
              alt={item.name || ''}
              draggable={false}
              style={goodsImageStyle(c)}
            />
          ) : (
            <span aria-hidden="true">🎁</span>
          )}
        </div>
        <div className="admin-goods__card-details">
          <h3 title={item.name || words('Item name', 'Название товара')}>
            {item.name || words('Item name', 'Название товара')}
          </h3>
          <div className="admin-goods__price">
            <strong>
              {new Intl.NumberFormat(ru ? 'ru-RU' : 'en-US', {
                style: 'currency',
                currency: /^[A-Z]{3}$/.test(item.currency || '') ? item.currency : 'RUB',
                maximumFractionDigits: 2,
              }).format(Number(item.price) || 0)}
            </strong>
            {item.link && /^https?:\/\//i.test(item.link) ? (
              <a
                href={goodsItemLink(item.link, item.erid)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={words('Explore item', 'Подробнее о товаре')}
              >
                <span className="admin-goods__link-label">{words('Explore', 'Подробнее')}</span>{' '}
                <span aria-hidden="true">↗</span>
              </a>
            ) : (
              <span aria-hidden="true">
                <span className="admin-goods__link-label">
                  {preview ? words('Explore', 'Подробнее') : ''}
                </span>{' '}
                ↗
              </span>
            )}
          </div>
        </div>
      </article>
    );
  }
  return (
    <section className="admin-goods" aria-label={words('Goods', 'Товары')}>
      <div
        className="admin-goods__advertiser-tabs"
        role="tablist"
        aria-label={words('Advertisers', 'Рекламодатели')}
      >
        {advertisers.map((advertiser) => (
          <button
            key={advertiser.id}
            role="tab"
            aria-selected={!newAdvertiser && selectedAdvertiserId === advertiser.id}
            disabled={busy || uploading || dirty}
            onClick={() => {
              resetForm();
              setNewAdvertiser(false);
              setSelectedAdvertiserId(advertiser.id);
            }}
          >
            {advertiser.storeBrandName}{' '}
            <span>{items.filter((item) => item.goodsAdvertiserId === advertiser.id).length}</span>
            {pendingBadge(items.filter((item) => item.goodsAdvertiserId === advertiser.id))}
          </button>
        ))}
        {items.some((item) => !item.goodsAdvertiserId) && (
          <button
            role="tab"
            aria-selected={!newAdvertiser && selectedAdvertiserId === null}
            disabled={busy || uploading || dirty}
            onClick={() => {
              resetForm();
              setNewAdvertiser(false);
              setSelectedAdvertiserId(null);
            }}
          >
            {words('Unassigned', 'Без рекламодателя')}
          </button>
        )}
        {newAdvertiser && (
          <button role="tab" aria-selected="true">
            {words('New advertiser', 'Новый рекламодатель')}
          </button>
        )}
        {isSuperAdmin && (
          <Button
            variant="ghost"
            size="small"
            disabled={busy || uploading || dirty}
            onClick={() => {
              setNewAdvertiser(true);
              editAdvertiser(ru, undefined, advertiserSaved, isSuperAdmin);
            }}
          >
            + {words('Advertiser', 'Рекламодатель')}
          </Button>
        )}
      </div>
      <div className="admin-goods__layout">
        <div className="admin-goods__catalog">
          <div className="admin-goods__heading">
            <div>
              <h2>
                {newAdvertiser
                  ? words('New advertiser', 'Новый рекламодатель')
                  : selectedAdvertiser?.storeBrandName ||
                    words('Unassigned', 'Без рекламодателя')}{' '}
                · {words('Goods', 'Товары')}
                {!loading && <span className="admin-goods__count">{visibleItems.length}</span>}
              </h2>
              <p>
                {words(
                  'Add items on the right. Preview your changes directly in the catalog.',
                  'Добавляйте товары справа. Изменения сразу видны в каталоге.',
                )}
              </p>
            </div>
            <button
              type="button"
              className="admin-goods__advertiser-summary"
              disabled={busy || uploading}
              onClick={() =>
                editAdvertiser(
                  ru,
                  newAdvertiser ? undefined : selectedAdvertiser,
                  advertiserSaved,
                  isSuperAdmin,
                )
              }
            >
              {selectedAdvertiser && !newAdvertiser ? (
                <>
                  <strong>
                    {selectedAdvertiser.advertiserLegalName || selectedAdvertiser.storeBrandName}
                  </strong>
                  <span>
                    {words('Tax ID', 'ИНН')}: {selectedAdvertiser.advertiserTaxId || '—'}
                  </span>
                  {!selectedAdvertiser.active && <span>{words('Inactive', 'Неактивен')}</span>}
                </>
              ) : (
                words('Add advertiser information', 'Добавить информацию о рекламодателе')
              )}
            </button>
          </div>
          {error && (
            <div role="alert" className="admin-goods__error">
              {error}{' '}
              <button
                onClick={() => {
                  setError('');
                  setLoading(true);
                  setRevision((n) => n + 1);
                }}
              >
                {words('Retry', 'Повторить')}
              </button>
            </div>
          )}

          {loading && <p role="status">{words('Loading goods…', 'Загрузка товаров…')}</p>}
          <div className="admin-goods__grid">
            {editingId === null && (
              <div className="admin-goods__draft" data-testid="goods-draft-card">
                <span className="admin-goods__draft-label">
                  {words('New item · Not saved', 'Новый товар · Не сохранён')}
                </span>
                {card(draft, true)}
              </div>
            )}
            {visibleItems.filter((item) => item.active).map(managedCard)}
          </div>
          {visibleItems.some((item) => !item.active) && (
            <details className="admin-goods__archive" open>
              <summary>
                {words('Archived goods', 'Архивные товары')} ·{' '}
                {visibleItems.filter((item) => !item.active).length}
              </summary>
              <div className="admin-goods__grid">
                {visibleItems.filter((item) => !item.active).map(managedCard)}
              </div>
            </details>
          )}
        </div>
        <form className="admin-goods__editor" ref={form} onSubmit={save}>
          <fieldset disabled={busy || uploading || newAdvertiser || !selectedAdvertiser}>
            <legend className="admin-goods__form-heading">
              <span>
                {editingId
                  ? words('Edit item', 'Редактировать товар')
                  : words('New item', 'Новый товар')}
              </span>
              <Button type="submit" variant="primary" loading={busy}>
                {busy ? words('Saving…', 'Сохранение…') : words('Save item', 'Сохранить товар')}
              </Button>
            </legend>
            <div className="admin-goods__copy-guide">
              <MarketExtensionGuide ru={ru} />
            </div>
            <input
              type="file"
              hidden
              ref={fileInput}
              aria-label={words('Upload image', 'Загрузить изображение')}
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                void upload(file);
              }}
            />
            <div className="admin-goods__compose">
              <section className="admin-goods__panel admin-goods__actions-panel">
                <h3>{words('Actions', 'Действия')}</h3>
                <div className="admin-goods__panel-actions">
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    icon={<TrashIcon />}
                    onClick={resetForm}
                  >
                    {words('Clear item', 'Очистить товар')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    icon={<DownloadIcon />}
                    onClick={() => void pasteProduct()}
                    disabled={busy || uploading}
                  >
                    {words('Paste product', 'Вставить товар')}
                  </Button>
                  {editingId !== null && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="small"
                      icon={<ArchiveIcon />}
                      onClick={confirmArchive}
                    >
                      {items.find((item) => item.id === editingId)?.active
                        ? words('Archive item', 'Архивировать товар')
                        : words('Restore item', 'Восстановить товар')}
                    </Button>
                  )}
                </div>
              </section>
              <section className="admin-goods__panel admin-goods__image-panel">
                <h3>{words('Product image', 'Изображение товара')}</h3>
                <GoodsImageEditor
                  onSelectFile={() => fileInput.current?.click()}
                  onDropFile={(file) => void upload(file)}
                  onRemove={() => {
                    setDraft((old) => ({
                      ...old,
                      image: '',
                      imageConfig: { x: 50, y: 50, scale: 1 },
                    }));
                    if (fileInput.current) fileInput.current.value = '';
                  }}
                  image={draft.image}
                  config={draft.imageConfig}
                  disabled={busy || uploading || newAdvertiser || !selectedAdvertiser}
                  ru={ru}
                  onChange={(imageConfig) => {
                    setDraft((old) => ({ ...old, imageConfig }));
                  }}
                />
                <label>
                  {words('Image URL', 'Ссылка на изображение')}
                  <input
                    type="url"
                    maxLength={4096}
                    placeholder="https://"
                    value={draft.image}
                    onChange={(e) => change('image', e.target.value)}
                  />
                </label>
              </section>
              <div className="admin-goods__field-panels">
                <section className="admin-goods__panel">
                  <h3>{words('Basics', 'Основное')}</h3>
                  <div className="admin-goods__import">
                    <label>
                      {words('Destination URL', 'Ссылка на товар')}
                      <input
                        type="url"
                        maxLength={4096}
                        placeholder="https://"
                        value={draft.link}
                        onChange={(e) => change('link', e.target.value)}
                      />
                    </label>

                    {pasteOpen && (
                      <div className="admin-goods__paste">
                        <label>
                          {words(
                            'Paste data from the Santa extension',
                            'Вставьте данные из расширения Santa',
                          )}
                          <textarea
                            rows={3}
                            value={pasteText}
                            onChange={(e) => setPasteText(e.target.value)}
                          />
                        </label>
                        <button
                          type="button"
                          disabled={!pasteText.trim()}
                          onClick={() => applyClipboard(pasteText)}
                        >
                          {words('Apply product', 'Заполнить товар')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPasteOpen(false);
                            setPasteText('');
                          }}
                        >
                          {words('Cancel', 'Отмена')}
                        </button>
                      </div>
                    )}
                    {importNote && (
                      <p className="admin-goods__hint" role="status">
                        {importNote}
                      </p>
                    )}
                  </div>
                  <label>
                    <span>
                      {words('Name', 'Название')}{' '}
                      <span className="admin-goods__required" aria-hidden="true">
                        *
                      </span>
                    </span>
                    <textarea
                      rows={2}
                      className="admin-goods__name"
                      required
                      maxLength={256}
                      value={draft.name}
                      onChange={(e) => change('name', e.target.value)}
                    />
                  </label>
                </section>
                <section className="admin-goods__panel">
                  <h3>{words('Price and currency', 'Цена и валюта')}</h3>
                  <div className="admin-goods__row">
                    <label>
                      <span>
                        {words('Price', 'Цена')}{' '}
                        <span className="admin-goods__required" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <input
                        required
                        type="number"
                        min="0"
                        max="999999999999.99"
                        step="0.01"
                        value={draft.price}
                        onChange={(e) => change('price', e.target.value)}
                      />
                    </label>
                    <label>
                      <span>
                        {words('Currency', 'Валюта')}{' '}
                        <span className="admin-goods__required" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <select
                        required
                        disabled={rublesOnly}
                        value={draft.currency}
                        onChange={(e) => change('currency', e.target.value)}
                      >
                        {['RUB', 'USD', 'EUR', 'GBP', 'KZT', 'BYN', 'AMD', 'GEL'].map((v) => (
                          <option key={v}>{v}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </section>
                <section className="admin-goods__panel">
                  <h3>{words('Advertising details', 'Рекламные данные')}</h3>
                  <div className="admin-goods__row">
                    <label>
                      ERID
                      <input
                        maxLength={256}
                        value={draft.erid || ''}
                        onChange={(e) => change('erid', e.target.value)}
                      />
                    </label>
                  </div>
                </section>
              </div>
            </div>
          </fieldset>
          {uploading && <p role="status">{words('Uploading…', 'Загрузка изображения…')}</p>}
        </form>
      </div>
    </section>
  );
}
